import React, { useState } from 'react';
import { V4 } from '../tokens';

// ── InputFieldV4 ── genérico (Components.jsx pattern)
interface InputFieldV4Props {
  label?: string;
  value: string;
  onChange: (val: string) => void;
  error?: string;
  pre?: React.ReactNode;
  post?: React.ReactNode;
  type?: string;
  placeholder?: string;
}

export function InputFieldV4({
  label,
  value,
  onChange,
  error,
  pre,
  post,
  type = 'text',
  placeholder,
}: InputFieldV4Props) {
  const bd = error ? V4.dangerAlt : V4.primary;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 10 }}>
      {label && (
        <span
          style={{
            fontWeight: 500,
            fontSize: 15,
            color: error ? V4.dangerAlt : V4.primary,
            marginBottom: 4,
          }}
        >
          {label}:
        </span>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: 6,
          overflow: 'hidden',
          border: `1px solid ${bd}`,
          background: V4.white,
        }}
      >
        {pre && (
          <span
            style={{
              background: V4.primary,
              color: V4.white,
              padding: '8px 10px',
              fontSize: 15,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {pre}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1,
            border: 0,
            outline: 0,
            padding: '8px 10px',
            font: 'inherit',
            color: V4.primary,
            background: 'transparent',
            fontSize: 15,
          }}
        />
        {post && (
          <span
            style={{
              background: V4.primary,
              color: V4.white,
              padding: '8px 10px',
              fontSize: 15,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {post}
          </span>
        )}
      </div>
      {error && <span style={{ fontSize: 12, color: V4.dangerAlt, fontWeight: 700, marginTop: 2 }}>Requerido</span>}
    </div>
  );
}

// ── TextInputV4 ── para DatosComercio sections (DatosComercio.jsx pattern)
interface TextInputV4Props {
  value: string;
  onChange: (val: string | number) => void;
  placeholder?: string;
  type?: string;
  prefix?: React.ReactNode;
  iconLeft?: React.ReactNode;
  suffix?: React.ReactNode;
  disabled?: boolean;
  // Passthrough opcionales y aditivos: habilitan autofill, teclados numéricos y cajas
  // de solo-lectura (copiar enlace) sin cambiar el comportamiento por defecto.
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  readOnly?: boolean;
  name?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  'aria-label'?: string;
}

export function TextInputV4({
  value,
  onChange,
  placeholder,
  type = 'text',
  prefix,
  iconLeft,
  suffix,
  disabled = false,
  autoComplete,
  inputMode,
  maxLength,
  readOnly = false,
  name,
  onFocus,
  'aria-label': ariaLabel,
}: TextInputV4Props) {
  const [focused, setFocused] = useState(false);
  const bd = focused && !disabled ? V4.secondary : V4.grayLight;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        border: `1.5px solid ${bd}`,
        borderRadius: 8,
        background: disabled ? V4.grayUltraLight : V4.white,
        overflow: 'hidden',
        transition: 'border-color 150ms',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {prefix && (
        <span
          style={{
            background: V4.grayUltraLight,
            color: V4.grayUltraDark,
            padding: '0 12px',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            borderRight: `1px solid ${V4.grayLight}`,
          }}
        >
          {prefix}
        </span>
      )}
      {iconLeft && (
        <span
          style={{
            padding: '0 10px 0 12px',
            color: V4.grayUltraDark,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {iconLeft}
        </span>
      )}
      <input
        type={type}
        value={value || ''}
        disabled={disabled}
        readOnly={readOnly}
        name={name}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={() => setFocused(false)}
        style={{
          flex: 1,
          border: 0,
          outline: 0,
          padding: iconLeft ? '10px 12px 10px 0' : '10px 12px',
          fontSize: 15,
          color: V4.primary,
          background: 'transparent',
          fontFamily: 'inherit',
          cursor: disabled ? 'default' : 'text',
        }}
      />
      {suffix && (
        <span
          style={{
            background: V4.grayUltraLight,
            color: V4.grayUltraDark,
            padding: '0 12px',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            borderLeft: `1px solid ${V4.grayLight}`,
            whiteSpace: 'nowrap',
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

// ── FieldV4 ── label wrapper (DatosComercio pattern)
interface FieldV4Props {
  label: React.ReactNode;
  helper?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

export function FieldV4({ label, helper, required, error, children }: FieldV4Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: error ? V4.danger : V4.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {label}
        {required && <span style={{ color: V4.danger }}>*</span>}
      </label>
      {children}
      {helper && !error && <span style={{ fontSize: 12, color: V4.grayUltraDark }}>{helper}</span>}
      {error && <span style={{ fontSize: 12, color: V4.danger, fontWeight: 600 }}>{error}</span>}
    </div>
  );
}
